import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardBody, Button, Table, Collapse, Input, FormGroup, Label } from 'reactstrap';
import api from '../../services/api';
import moment from 'moment';
import NewScheduleDateModal from './NewScheduleDateModal';
import EditScheduleDateModal from './EditScheduleDateModal';
import BulkScheduleDatesModal from './BulkScheduleDatesModal';
import BulkMoveScheduleDatesModal from './BulkMoveScheduleDatesModal';

const LeagueScheduleDates = ({ schedule, leagueId }) => {
  const [dates, setDates] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [filterWeek, setFilterWeek] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [newDateModal, setNewDateModal] = useState(false);
  const [editDateModal, setEditDateModal] = useState(false);
  const [bulkDateModal, setBulkDateModal] = useState(false);
  const [bulkMoveModal, setBulkMoveModal] = useState(false);
  const [editingDate, setEditingDate] = useState(null);

  const toggle = () => setIsOpen(!isOpen);
  const toggleNewModal = () => setNewDateModal(!newDateModal);
  const toggleEditModal = () => setEditDateModal(!editDateModal);
  const toggleBulkModal = () => setBulkDateModal(!bulkDateModal);
  const toggleBulkMoveModal = () => setBulkMoveModal(!bulkMoveModal);

  const fetchDates = useCallback(async () => {
    try {
      const response = await api.get(`/league-schedule-dates/?schedule=${schedule.id}`);
      setDates(response.data);
    } catch (error) {
      console.error('Error fetching schedule dates:', error);
    }
  }, [schedule.id]);

  const fetchWeeks = useCallback(async () => {
    try {
      const response = await api.get(`/league-weeks/?league=${leagueId}`);
      setWeeks(response.data);
    } catch (error) {
      console.error('Error fetching weeks:', error);
    }
  }, [leagueId]);

  useEffect(() => {
    if (schedule?.id) {
      fetchDates();
    }
  }, [schedule?.id, fetchDates]);

  useEffect(() => {
    if (leagueId) {
      fetchWeeks();
    }
  }, [leagueId, fetchWeeks]);

  const handleAddDate = async (dateData) => {
    try {
      await api.post('/league-schedule-dates/', dateData);
      fetchDates();
      toggleNewModal();
    } catch (error) {
      console.error('Error adding date:', error);
      alert('Error adding date: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleBulkAdd = async (bulkData) => {
    try {
      // Generate all dates between start and end that match the day of week
      const startDate = moment(bulkData.start_date);
      const endDate = moment(bulkData.end_date);
      const dayOfWeek = parseInt(bulkData.day_of_week);
      const datesToAdd = [];

      let currentDate = startDate.clone();
      while (currentDate.isSameOrBefore(endDate)) {
        if (currentDate.day() === dayOfWeek) {
          datesToAdd.push({
            schedule: bulkData.schedule,
            date: currentDate.format('YYYY-MM-DD'),
            max_date: bulkData.max_date || null
          });
        }
        currentDate.add(1, 'day');
      }

      if (datesToAdd.length === 0) {
        alert('No dates match the selected day of week in the specified range.');
        return;
      }

      // Make multiple POST requests
      const promises = datesToAdd.map(dateData => 
        api.post('/league-schedule-dates/', dateData)
      );
      
      await Promise.all(promises);
      fetchDates();
      toggleBulkModal();
    } catch (error) {
      console.error('Error bulk adding dates:', error);
      alert('Error bulk adding dates: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditDate = (date) => {
    setEditingDate(date);
    toggleEditModal();
  };

  const handleBulkMoveDates = async (moveData) => {
    try {
      if (dates.length === 0) {
        alert('No schedule dates exist to move.');
        return;
      }

      const selectedDayOfWeek = parseInt(moveData.day_of_week, 10);
      let currentDate = moment(moveData.start_date);

      while (currentDate.day() !== selectedDayOfWeek) {
        currentDate.add(1, 'day');
      }

      const sortedDates = [...dates].sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());

      for (const existingDate of sortedDates) {
        const newDate = currentDate.clone();
        const originalDate = moment(existingDate.date);
        const originalMaxDate = existingDate.max_date ? moment(existingDate.max_date) : null;
        const maxDateOffset = originalMaxDate ? originalMaxDate.diff(originalDate, 'days') : null;

        await api.put(`/league-schedule-dates/${existingDate.id}/`, {
          schedule: schedule.id,
          league_week: existingDate.league_week || null,
          date: newDate.format('YYYY-MM-DD'),
          max_date: maxDateOffset !== null ? newDate.clone().add(maxDateOffset, 'days').format('YYYY-MM-DD') : null
        });

        currentDate.add(7, 'days');
      }

      fetchDates();
      toggleBulkMoveModal();
    } catch (error) {
      console.error('Error bulk moving dates:', error);
      alert('Error bulk moving dates: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSaveDate = async (dateData) => {
    try {
      await api.put(`/league-schedule-dates/${editingDate.id}/`, dateData);
      fetchDates();
      toggleEditModal();
      setEditingDate(null);
    } catch (error) {
      console.error('Error updating date:', error);
      alert('Error updating date: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteDate = async (dateId) => {
    if (!window.confirm('Are you sure you want to delete this date?')) {
      return;
    }
    try {
      await api.delete(`/league-schedule-dates/${dateId}/`);
      fetchDates();
    } catch (error) {
      console.error('Error deleting date:', error);
      alert('Error deleting date: ' + (error.response?.data?.detail || error.message));
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return moment(date).format('MMM DD, YYYY');
  };

  const getWeekNumber = (date) => {
    if (!date.league_week) return '-';
    const week = weeks.find(w => w.id === date.league_week);
    return week ? week.week : date.week || '-';
  };

  const filteredDates = filterWeek 
    ? dates.filter(d => d.league_week === parseInt(filterWeek))
    : dates;

  if (!schedule?.id) {
    return null;
  }

  return (
    <>
      <Card className="mt-2 border-secondary">
        <CardHeader
          onClick={toggle}
          className="cursor-pointer bg-light"
          style={{ cursor: 'pointer' }}
        >
          <small className="fw-semibold">
            {isOpen ? '▼' : '▶'} Schedule Dates ({dates.length})
          </small>
        </CardHeader>

        <Collapse isOpen={isOpen}>
          <CardBody className="p-2">
            <FormGroup className="mb-2">
              <Label for="filterWeek" className="small">Filter by Week:</Label>
              <Input
                type="select"
                id="filterWeek"
                bsSize="sm"
                value={filterWeek}
                onChange={(e) => setFilterWeek(e.target.value)}
              >
                <option value="">All Weeks</option>
                {weeks.map(w => (
                  <option key={w.id} value={w.id}>
                    Week {w.week} {w.notes && `(${w.notes})`}
                  </option>
                ))}
              </Input>
            </FormGroup>

            {filteredDates.length === 0 ? (
              <p className="text-muted small mb-2">No dates configured for this schedule.</p>
            ) : (
              <Table bordered hover size="sm" className="mb-2">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '80px' }}>Week</th>
                    <th>Date</th>
                    <th>Max Date</th>
                    <th style={{ width: '120px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDates.map((date) => (
                    <tr key={date.id}>
                      <td className="text-center">{getWeekNumber(date)}</td>
                      <td>{formatDate(date.date)}</td>
                      <td>{date.max_date ? formatDate(date.max_date) : <em className="text-muted">-</em>}</td>
                      <td>
                        <Button color="primary" size="sm" onClick={() => handleEditDate(date)}>
                          Edit
                        </Button>{' '}
                        <Button color="danger" size="sm" onClick={() => handleDeleteDate(date.id)}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <div>
              <Button color="success" size="sm" onClick={toggleNewModal}>
                + Add Date
              </Button>{' '}
              <Button outline color="dark" size="sm" onClick={toggleBulkModal}>
                Bulk Add Dates
              </Button>{' '}
              <Button outline color="primary" size="sm" onClick={toggleBulkMoveModal} disabled={dates.length === 0}>
                Bulk Move Dates
              </Button>
            </div>
          </CardBody>
        </Collapse>
      </Card>

      <NewScheduleDateModal
        isOpen={newDateModal}
        toggle={toggleNewModal}
        onSave={handleAddDate}
        scheduleId={schedule?.id}
        leagueId={leagueId}
      />

      <EditScheduleDateModal
        isOpen={editDateModal}
        toggle={toggleEditModal}
        onSave={handleSaveDate}
        scheduleDate={editingDate}
        leagueId={leagueId}
        scheduleId={schedule?.id}
      />

      <BulkScheduleDatesModal
        isOpen={bulkDateModal}
        toggle={toggleBulkModal}
        onSave={handleBulkAdd}
        scheduleId={schedule?.id}
        leagueId={leagueId}
      />

      <BulkMoveScheduleDatesModal
        isOpen={bulkMoveModal}
        toggle={toggleBulkMoveModal}
        onSave={handleBulkMoveDates}
        disabled={dates.length === 0}
      />
    </>
  );
};

export default LeagueScheduleDates;
